import { useEffect } from 'react';
import ReactGA from 'react-ga';
import { BrowserRouter as Router, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import SplitOrderWrapper from '@assets/ui-designs/sprints/split-order/Split-orderWrapper';
import ComponentWrapper from '@assets/ui-designs/ui-components/ComponentWrapper';
import MaterialWrapper from '@assets/ui-designs/sprints/material-requisition/MaterialWrapper';
import TaskConfiWrapper from '@assets/ui-designs/sprints/task/TaskConfiWrapper';
import TaskWrapper from '@assets/ui-designs/sprints/my-project/TaskWrapper';

import TaskManagementCalendar from '@features/task-management-calendar/TaskManagementCalendar';

import AddAdmin from '@pages/admin/AddAdmin';
import AddRole from '@pages/admin/AddRole';
import AddTeam from '@pages/admin/AddTeam';
import Admin from '@pages/admin/Admin';
import EditAdmin from '@pages/admin/EditAdmin';
import EditRole from '@pages/admin/EditRole';
import EditTeam from '@pages/admin/EditTeam';
import AssetsWrapper from '@pages/assets/AssetsWrapper';
import ForgetPassword from '@pages/auth/ForgetPassword';
import Login from '@pages/auth/Login';
import Logout from '@pages/auth/Logout';
import MyProfile from '@pages/auth/MyProfile';
import ReleaseNotes from '@pages/reports/ReleaseNotes';
import ResetPassword from '@pages/auth/ResetPassword';
import EditBrand from '@pages/brands/EditBrand';
import { BuyerRequests } from '@pages/buyer-request/BuyerRequests';
import CollectionRequestContainer from '@pages/buyer-request/CollectionRequestContainer';
import OrderDetails from '@pages/buyer-request/OrderDetails';
import QuoteRequestDetails from '@pages/buyer-request/QuoteRequestDetails';
import SampleRequestDetails from '@pages/buyer-request/SampleRequestDetails';
import AddClient from '@pages/client/AddClient';
import EditClient from '@pages/client/EditClient';
import ManageClient from '@pages/client/ManageClient';
import CollectionView from '@pages/collection/CollectionDetails';
import SetOrderDetails from '@pages/collection/SetOrderDetails';
import Configuration from '@pages/configuration/Configuration';
import CostingDetails from '@pages/costing/CostingDetails';
import ListOfCosting from '@pages/costing/ListOfCosting';
import Dashboard from '@pages/dashboard/Dashboard';
import DesignEdit from '@pages/design/DesignEdit';
import DesignList from '@pages/design/DesignList';
import DesignView from '@pages/design/DesignView';
import DesignTool from '@pages/designTool/DesignTool';
import Techpack from '@pages/designTool/Techpack';
import Favourite from '@pages/favourites/Favourite';
import FavouriteDetails from '@pages/favourites/FavouriteDetails';
import MaterialDetails from '@pages/materials/MaterialDetails';
import Materials from '@pages/materials/Materials';
import MeasurementTemplate from '@pages/measurement/MeasurementTemplate';
import CreateOrder from '@pages/order/CreateOrder';
import MyOrder from '@pages/order/MyOrder';
import MyOrderDetails from '@pages/order/MyOrderDetails';
import ListOfCollection from '@pages/photography/ListOfCollection';
import PomList from '@pages/pom/PomList';
import PomTagList from '@pages/pom/PomTagList';
import UserReport from '@pages/reports/UserReport';
import SampleRequestList from '@pages/sampleDevelopment/SampleRequestList';
import SampleView from '@pages/sampleDevelopment/SampleView';
import SupplierOnboarding from '@pages/supplier-onboarding/SupplierOnboarding';
import AddSupplier from '@pages/supplier/AddSupplier';
import SupplierDetails from '@pages/supplier/SupplierDetails';
import SupplierList from '@pages/supplier/SupplierList';
import ImageSearch from '@pages/trend-research/ImageSearch';
import MoodBoards from '@pages/trend-research/Moodboard';
import MoodBoardDetails from '@pages/trend-research/MoodboardDetails';
import SimilarProducts from '@pages/trend-research/SimilarProducts';
import TrendOnboarding from '@pages/trend-research/TrendOnboarding';
import MyProjectPage from '@pages/project-management-tools/MyProjectPage';
import MyProjectDetailsPage from '@pages/project-management-tools/MyProjectDetailsPage';
import PoAutomationDetails from '@pages/order/PoAutomationDetails';

import AuthLayout from '@layouts/AuthLayout';
import DefaultLayout from '@layouts/DefaultLayout';

import ApiClient from '@lib/apiClient';

// import { isAdmin } from '@utils/Util';

import { BASE_FRONT_END_URL, BASE_URL, ROUTES } from '@/constants/constant';

import TaskConfigurationDetails from '@/pages/task-configuration/TaskConfigurationDetails';
import BuyersActivity from '@/pages/buyers-activity/BuyersActivity';
import BrandActivity from '@/pages/buyers-activity/BrandActivity';
import NitexStudio from '@/pages/nitex-studio/NitexStudio';
import DashboardWrapper from '@/assets/ui-designs/sprints/dashboard/DashboardWrapper';
import MyDashboard from '@/pages/my-dashboard/MyDashboard';

const redirectBasedOnRole = () => {
	// // For non-admin users, redirect to My Dashboard
	// if (!isAdmin()) {
	// 	return <Navigate to="/my-dashboard" />;
	// }

	// For admin users, redirect to their first menu item
	let menusString = localStorage.getItem('orderOfMenus');
	let menus = ['/'];
	if (menusString) {
		menus = JSON.parse(menusString);
	}
	return <Navigate to={ROUTES[menus[0]]} />;
};

const addPageVisitedEvent = (path) => {
	ReactGA.pageview(window.location.pathname + window.location.search);
	let body = { path: path, baseUrl: BASE_URL, baseFrontEndUrl: BASE_FRONT_END_URL };
	ApiClient.POST('mixpanelAddEvent', body, 'PAGE_VISITED');
};

const AuthRoute = () => {
	const token = localStorage.getItem('token');
	return token == null ? <Outlet /> : redirectBasedOnRole();
};

const PublicRoute = () => {
	const token = localStorage.getItem('token');
	const { pathname } = useLocation();
	addPageVisitedEvent(pathname);
	return token ? <Outlet /> : <Navigate to="/login" />;
};

const ScrollToTop = () => {
	const { pathname } = useLocation();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [pathname]);

	return null;
};

const AppRoutes = () => {
	return (
		<Router>
			<ScrollToTop />
			<Routes>
				<Route path="/logout" element={<Logout />} />
				<Route path="/techpack/:id" element={<Techpack isGlobal={true} />} />

				<Route path="/" element={<AuthRoute />}>
					<Route element={<AuthLayout />}>
						<Route path="/" element={<Login />} />
						<Route path="/login" element={<Login />} />
					</Route>
					<Route path="/forget-password" element={<ForgetPassword />} />
					<Route path="/reset-password" element={<ResetPassword />} />
				</Route>

				<Route path="/" element={<PublicRoute />}>
					<Route element={<DefaultLayout />}>
						<Route path="/dashboard" element={<Dashboard />} />
						<Route path="/my-dashboard" element={<MyDashboard />} />
						<Route path="/my-profile" element={<MyProfile />} />
						<Route path="/release-notes" element={<ReleaseNotes />} />
						<Route path="/admins/list" element={<Admin />} />
						<Route path="/admins/add" element={<AddAdmin />} />
						<Route path="/admins/edit/:id" element={<EditAdmin />} />
						<Route path="/clients/list" element={<ManageClient />} />
						<Route path="/clients/add" element={<AddClient />} />
						<Route path="/clients/edit/:id" element={<EditClient />} />
						<Route path="/collections/list" element={<DesignList />} />
						<Route path="/nitex-studio" element={<NitexStudio />} />
						<Route path="/designs/modalview/:id" element={<DesignView />} />
						<Route path="/orders/create" element={<CreateOrder />} />
						<Route path="/orders/my-orders" element={<MyOrder />} />
						<Route path="/orders/view/:id" element={<MyOrderDetails />} />
						<Route path="/orders/po-automation" element={<PoAutomationDetails />} />
						<Route path="/collections/view/:id" element={<CollectionView />} />
						<Route path="/set/view/:id" element={<SetOrderDetails />} />
						<Route path="/roles/add" element={<AddRole />} />
						<Route path="/roles/edit/:id" element={<EditRole />} />
						<Route path="/teams/add" element={<AddTeam />} />
						<Route path="/teams/edit/:id" element={<EditTeam />} />
						<Route path="/brands/edit/:id" element={<EditBrand />} />
						<Route path="/suppliers/add" element={<AddSupplier />} />
						<Route path="/suppliers/view/:id" element={<SupplierDetails />} />
						<Route path="/materials/view" element={<Materials />} />
						<Route path="/materials/view/:id" element={<MaterialDetails />} />
						<Route path="/suppliers/view" element={<SupplierList />} />
						<Route path="/reports" element={<UserReport />} />
						<Route path="/sample/all" element={<SampleRequestList />} />
						<Route path="/sample/view/:id" element={<SampleView />} />
						<Route path="/favourite/list" element={<Favourite />} />
						<Route path="/favourite/:id" element={<FavouriteDetails />} />
						<Route path="/photography/all" element={<ListOfCollection />} />
						<Route path="/costing/initial" element={<ListOfCosting />} />
						<Route path="/costing/:id" element={<CostingDetails />} />
						<Route path="/buyer-requests" element={<BuyerRequests />} />
						<Route path="/sample/request/:id" element={<SampleRequestDetails />} />
						<Route path="/quote/request/:id" element={<QuoteRequestDetails />} />
						<Route path="/order/request/:id" element={<OrderDetails />} />
						<Route path="/collection/request/:id" element={<CollectionRequestContainer />} />

						<Route path="/suppliers/profile/:id" element={<SupplierOnboarding />} />
						<Route path="/configuration" element={<Configuration />} />
						<Route path="/configuration/pom-list" element={<PomList />} />
						<Route path="/configuration/measurement-template" element={<MeasurementTemplate />} />
						<Route path="/pom-tag-list/:id" element={<PomTagList />} />
						<Route path="/assets" element={<AssetsWrapper />} />
						<Route path="/trends" element={<TrendOnboarding />} />
						<Route path="/trends/image-search" element={<ImageSearch />} />
						<Route path="/moodboards" element={<MoodBoards />} />
						<Route path="/moodboard/:id" element={<MoodBoardDetails />} />
						<Route path="/trends/similar-products" element={<SimilarProducts />} />
						<Route path="/my-project" element={<MyProjectPage />} />
						<Route path="/my-project/:id" element={<MyProjectDetailsPage />} />
						<Route path="/configuration/task" element={<TaskConfigurationDetails />} />
						<Route path="/calendar" element={<TaskManagementCalendar />} />
						<Route path="/buyers-activity" element={<BuyersActivity />} />
						<Route path="/brands-activity" element={<BrandActivity />} />
					</Route>
					<Route path="/designs/edit/:id" element={<DesignEdit />} />
					<Route path="/designTool/:id" element={<DesignTool />} />
				</Route>

				<Route path="/ui-components" element={<ComponentWrapper />} />
				<Route path="/ui-order" element={<SplitOrderWrapper />} />
				<Route path="/ui-material" element={<MaterialWrapper />} />
				<Route path="/ui-project" element={<TaskWrapper />} />
				<Route path="/ui-task" element={<TaskConfiWrapper />} />
				<Route path="/ui-dashboard" element={<DashboardWrapper />} />
			</Routes>
		</Router>
	);
};

export default AppRoutes;
